export const DropdownMenu = () => {
    const menus = document.querySelectorAll('.is-dropdown');

    menus.forEach((menu) => {
        new Dropdown(menu, {
            topLevelClickable: false,
        });
    });
};

class Dropdown {
    constructor(domNode, options = {}) {
        this.rootNode = domNode;
        this.openIndex = null;
        this.useArrowKeys = options.useArrowKeys ?? true;
        this.withArrowButton = options.withArrowButton ?? true;
        this.topLevelClickable = options.topLevelClickable ?? true;
        this.topLevelNodes = [...this.rootNode.children];
        this.topLevelButtons = [];
        this.submenus = [];
        this.dropdownPanels = [];
        this.timers = new Array(this.topLevelNodes.length).fill(null);
        this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

        this.topLevelNodes.forEach((node, index) => {
            this.initNode(node, index);
        });

        this.rootNode.addEventListener('focusout', this.onBlur.bind(this));
        this.rootNode.addEventListener('keydown', this.onLinkKeyDown.bind(this));

        // Calculate all overflows on init
        this.calculateAllOverflows();

        // Recalculate on resize (debounced)
        this.debouncedResize = this.debounce(this.calculateAllOverflows.bind(this), 100);
        window.addEventListener('resize', this.debouncedResize);
    }

    initNode(node, index) {
        const submenu = node.querySelector('ul');
        const link = node.querySelector('a');
        const dropdownPanel = node.querySelector(':scope > div.absolute') || node.querySelector(':scope > div[class*="absolute"]') || node.querySelector(':scope > .absolute');

        if (this.withArrowButton && submenu) {
            const id = uid();

            const button = document.createElement('button');
            button.setAttribute('type', 'button');
            button.setAttribute('aria-expanded', 'false');
            button.setAttribute('aria-controls', `id_${id}`);
            button.setAttribute('aria-label', `More ${link.textContent.trim()} pages`);
            button.classList.add('dropdown-arrow');

            submenu.setAttribute('id', `id_${id}`);

            button.innerHTML = `
                <svg aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 30.727 30.727">
                    <path fill="currentColor"
                        d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0 l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
                </svg>`;

            link.insertAdjacentElement('afterend', button);
        }

        const button = node.querySelector('button') ?? link;

        this.submenus.push(submenu ?? null);
        this.topLevelButtons.push(button);
        this.dropdownPanels.push(dropdownPanel ?? null);

        if (submenu) {
            if (!this.topLevelClickable && link) {
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.toggleExpand(index, this.openIndex !== index);
                });

                link.setAttribute('role', 'button');
                link.setAttribute('aria-expanded', 'false');
            }

            node.addEventListener('mouseover', () => {
                this.toggleExpand(index, true);
            });
            node.addEventListener('mouseout', () => {
                this.toggleExpand(index, false);
            });

            if (button !== link) {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.toggleExpand(index, this.openIndex !== index);
                });
            }

            button.addEventListener('keydown', this.onButtonKeyDown.bind(this, index));

            // Listen on dropdown panel to capture all keyboard events (including featured card)
            if (dropdownPanel) {
                dropdownPanel.addEventListener('keydown', this.onMenuKeyDown.bind(this, index));
            }
        }
    }

    /**
     * Calculate overflow for all dropdown panels
     */
    calculateAllOverflows() {
        this.dropdownPanels.forEach((panel, index) => {
            this.checkOverflow(index);
        });
    }

    /**
     * Check if dropdown overflows viewport and set CSS custom properties
     */
    checkOverflow(index) {
        const panel = this.dropdownPanels[index];
        if (!panel) return;

        // Reset value
        panel.style.setProperty('--offset-overflow', '0px');

        // Temporarily make visible for measurement
        const originalStyles = {
            visibility: panel.style.visibility,
            opacity: panel.style.opacity,
            pointerEvents: panel.style.pointerEvents,
            transition: panel.style.transition,
        };

        panel.style.visibility = 'hidden';
        panel.style.opacity = '0';
        panel.style.pointerEvents = 'none';
        panel.style.transition = 'none';

        // Force display
        panel.classList.add('!block');
        panel.offsetHeight;

        const rect = panel.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const padding = 16;

        // Calculate single offset value
        // Positive = overflow left, Negative = overflow right
        let offset = 0;

        if (rect.left < padding) {
            offset = padding - rect.left;
        } else if (rect.right > viewportWidth - padding) {
            offset = viewportWidth - padding - rect.right;
        }

        // Set CSS custom property (rounded)
        panel.style.setProperty('--offset-overflow', `${Math.round(offset)}px`);

        // Restore original styles
        panel.classList.remove('!block');
        panel.style.visibility = originalStyles.visibility;
        panel.style.opacity = originalStyles.opacity;
        panel.style.pointerEvents = originalStyles.pointerEvents;
        panel.style.transition = originalStyles.transition;
    }

    onLinkKeyDown(event) {
        const focusableElements = this.topLevelNodes
            .map((node) => {
                return Array.from(node.children).filter((child) => child.tagName === 'A' || child.tagName === 'BUTTON');
            })
            .flat();

        const targetLinkIndex = focusableElements.indexOf(document.activeElement);

        if (this.openIndex !== null && targetLinkIndex !== -1 && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            this.toggleExpand(this.openIndex, false);
        }

        this.controlFocusByKey(event, focusableElements, targetLinkIndex);
    }

    onMenuKeyDown(index, event) {
        if (this.openIndex === null) return;

        // Query from dropdownPanel to include all focusable elements (menus + featured card)
        const menuLinks = Array.from(this.dropdownPanels[this.openIndex].querySelectorAll(this.focusableElements));
        const currentIndex = menuLinks.indexOf(document.activeElement);
        const firstFocusableElement = menuLinks[0];
        const lastFocusableElement = menuLinks[menuLinks.length - 1];

        if (event.key === 'Escape') {
            this.topLevelButtons[this.openIndex].focus();
            this.toggleExpand(this.openIndex, false);
        }

        this.controlFocusByKey(event, menuLinks, currentIndex);

        if (event.key === 'Tab') {
            if (event.shiftKey && document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus();
                event.preventDefault();
            } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
                firstFocusableElement.focus();
                event.preventDefault();
            }
        }
    }

    onButtonKeyDown(index, event) {
        const targetButtonIndex = this.topLevelButtons.indexOf(document.activeElement);

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleExpand(index, this.openIndex !== index);
        }

        if (event.key === 'Escape') {
            this.toggleExpand(index, false);
        }

        if (this.openIndex === targetButtonIndex && event.key === 'ArrowDown') {
            event.preventDefault();
            const firstFocusable = this.dropdownPanels[this.openIndex].querySelector(this.focusableElements);
            if (firstFocusable) firstFocusable.focus();
        }
    }

    onBlur(event) {
        const menuContainsFocus = this.rootNode.contains(event.relatedTarget);
        if (!menuContainsFocus && this.openIndex !== null) {
            this.toggleExpand(this.openIndex, false);
        }
    }

    controlFocusByKey(keyboardEvent, nodeList, currentIndex) {
        switch (keyboardEvent.key) {
            case 'ArrowUp':
            case 'ArrowLeft':
                keyboardEvent.preventDefault();
                if (currentIndex > -1) {
                    const prevIndex = Math.max(0, currentIndex - 1);
                    nodeList[prevIndex].focus();
                }
                break;
            case 'ArrowDown':
            case 'ArrowRight':
                keyboardEvent.preventDefault();
                if (currentIndex > -1) {
                    const nextIndex = Math.min(nodeList.length - 1, currentIndex + 1);
                    nodeList[nextIndex].focus();
                }
                break;
            case 'Home':
                keyboardEvent.preventDefault();
                nodeList[0].focus();
                break;
            case 'End':
                keyboardEvent.preventDefault();
                nodeList[nodeList.length - 1].focus();
                break;
        }
    }

    toggleExpand(index, expanded) {
        if (this.openIndex !== null && this.openIndex !== index) {
            this.toggleExpand(this.openIndex, false);
        }

        if (this.topLevelButtons[index]) {
            this.openIndex = expanded ? index : null;

            if (this.timers[index]) {
                clearTimeout(this.timers[index]);
                this.timers[index] = null;
            }

            if (expanded) {
                this.topLevelButtons[index].setAttribute('aria-expanded', 'true');

                const link = this.topLevelNodes[index].querySelector('a');
                if (link?.hasAttribute('aria-expanded')) {
                    link.setAttribute('aria-expanded', 'true');
                }
            } else {
                this.timers[index] = setTimeout(() => {
                    this.topLevelButtons[index].setAttribute('aria-expanded', 'false');

                    const link = this.topLevelNodes[index].querySelector('a');
                    if (link?.hasAttribute('aria-expanded')) {
                        link.setAttribute('aria-expanded', 'false');
                    }
                }, 300);
            }
        }
    }

    /**
     * Debounce utility
     */
    debounce(func, wait) {
        let timeout;

        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }
}

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
